import React from 'react';
import { Link } from 'react-router-dom';
import { Flag, Lock, Handshake } from 'lucide-react';
import SEOHead, { breadcrumb } from './SEOHead';
import LandingFooter from './LandingFooter';
import './landing.css';

const serif = { fontFamily: '"DM Serif Display", Georgia, serif' };
const ACCENT = '#1a5fff';

const STATS = [
  ['2021', ACCENT, 'Founded in Pune'],
  ['50+', '#0ea98b', 'Companies trust us'],
  ['5K+', '#f59e0b', 'Employees managed'],
  ['9', '#f43f5e', 'HR modules'],
];

const VALUES = [
  { icon: Flag, iconColor: ACCENT, bg: '#e8efff', title: 'Built for India', desc: 'Every feature is designed around Indian labour laws, statutory compliance (PF, ESI, PT, TDS), and the way Indian teams actually work.' },
  { icon: Lock, iconColor: '#0ea98b', bg: '#e0f7f3', title: 'Privacy First', desc: 'Your employee data belongs to you. We are SOC 2 certified, GDPR-aligned, and never sell or share your data with third parties.' },
  { icon: Handshake, iconColor: '#d97706', bg: '#fef3c7', title: 'Customer Obsessed', desc: 'Every product decision starts with customer feedback. Our NPS consistently sits above 70 because we listen and act fast.' },
];

const About = () => (
  <>
    <SEOHead
      title="About DigiHR — HR Software Company in Pune, India"
      description="DigiHR is a cloud HRMS built by Sciverse Solutions Pvt. Ltd., Pune, to simplify attendance, payroll and people management for Indian businesses. Our story and values."
      canonical="/about"
      schema={breadcrumb('About', '/about')}
    />

    {/* Page Hero */}
    <section style={{ padding: '60px 5% 52px', background: 'linear-gradient(160deg,#f0f4ff 0%,#ffffff 100%)', borderBottom: '1px solid #e4e7ed' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Our Story</div>
        <h1 style={{ ...serif, fontSize: 'clamp(36px,4.5vw,56px)', lineHeight: 1.1, letterSpacing: '-.03em', color: '#0d1117', maxWidth: 640, marginBottom: 16 }}>
          Built by HR practitioners, <em style={{ fontStyle: 'italic', color: ACCENT }}>for Indian businesses</em>
        </h1>
        <p style={{ fontSize: 17, color: '#3a3f4a', lineHeight: 1.65, maxWidth: 520 }}>DigiHR was born out of frustration — watching great HR teams waste hours on spreadsheets, manual payroll and disconnected tools. We decided to fix that.</p>
      </div>
    </section>

    {/* Who we are */}
    <section style={{ padding: '80px 5%' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center', marginBottom: 72 }} className="about-who-resp">
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Who we are</div>
            <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117', marginBottom: 16 }}>
              A team that <em style={{ fontStyle: 'italic', color: ACCENT }}>understands</em> HR
            </h2>
            <p style={{ fontSize: 16, color: '#3a3f4a', lineHeight: 1.75, marginBottom: 16 }}>DigiHR is built and operated by Sciverse Solutions Pvt. Ltd., headquartered in Pune, Maharashtra. Our founders have a combined 30+ years of experience in HR technology, payroll compliance and enterprise software across India.</p>
            <p style={{ fontSize: 16, color: '#3a3f4a', lineHeight: 1.75 }}>We started with one belief: Indian businesses deserve HRMS software that is affordable, compliant, and genuinely easy to use — not adapted from foreign platforms that don't understand PF, ESI, or PT.</p>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e4e7ed', borderRadius: 28, boxShadow: '0 12px 40px rgba(13,17,23,.12)', padding: 28 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {STATS.map(([num, color, label]) => (
                <div key={label} style={{ background: '#f6f7f9', borderRadius: 20, padding: 24, textAlign: 'center' }}>
                  <div style={{ ...serif, fontSize: 40, color }}>{num}</div>
                  <div style={{ fontSize: 13, color: '#717888', marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Our Values</div>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117' }}>What we stand for</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }} className="about-values-resp">
          {VALUES.map(({ icon: Icon, iconColor, bg, title, desc }) => (
            <div key={title} style={{ background: '#fff', border: '1px solid #e4e7ed', borderRadius: 20, padding: '28px 28px 24px' }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}><Icon size={22} color={iconColor} strokeWidth={1.75} /></div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0d1117', marginBottom: 8 }}>{title}</h3>
              <p style={{ fontSize: 14, color: '#3a3f4a', lineHeight: 1.6 }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section style={{ padding: '80px 5%', background: 'linear-gradient(135deg,#0d1117 0%,#1a2540 100%)', textAlign: 'center', color: '#fff', position: 'relative', overflow: 'hidden' }} className="cta-band-radial">
      <div style={{ position: 'relative', maxWidth: 640, margin: '0 auto' }}>
        <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#fff', marginBottom: 14 }}>Want to work with us?</h2>
        <p style={{ fontSize: 17, color: 'rgba(255,255,255,.6)', marginBottom: 32 }}>We're always looking for talented people who care about making HR better in India.</p>
        <Link to="/careers" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '14px 32px', borderRadius: 20, background: ACCENT, color: '#fff', fontSize: 15, fontWeight: 500, textDecoration: 'none' }}>See open roles →</Link>
      </div>
    </section>

    <LandingFooter />

    <style>{`
      @media(max-width:900px){
        .about-who-resp{ grid-template-columns:1fr !important; }
        .about-values-resp{ grid-template-columns:1fr !important; }
      }
    `}</style>
  </>
);

export default About;
